import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead, Rise } from '../ui/screen-parts.js';
import { Chip, Empty, Modal, Segments, useFlash, useModal } from '../ui/kit.js';
import { Done, useLast } from '../ui/actions/parts.js';
import { RESIDENT } from '../state/building.js';

import Package0 from '../media/images/packages-screen/package-0.jpg';
import Package1 from '../media/images/packages-screen/package-1.jpg';
import Package2 from '../media/images/packages-screen/package-2.jpeg';
import Package3 from '../media/images/packages-screen/package-3.JPG';
import Package4 from '../media/images/packages-screen/package-4.jpg';
import Package5 from '../media/images/packages-screen/package-5.jpg';
import Package6 from '../media/images/packages-screen/package-6.jpg';
import Package7 from '../media/images/packages-screen/package-7.jpg';
import Package8 from '../media/images/packages-screen/package-8.jpeg';
import Package9 from '../media/images/packages-screen/package-9.webp';

const EASE = [0.16, 1, 0.3, 1];

const REVEAL = { duration: 0.4, ease: EASE };

/* When the desk can bring a package up. The first is whoever is free. */
const WINDOWS = ['Next available', '6 to 7 PM', '7 to 8 PM', '8 to 9 PM'];

/* A window as a sentence says it: "between 7 and 8 PM". */
const spoken = w => (w === WINDOWS[0] ? 'in about 20 minutes' : `between ${w.replace(' to ', ' and ')}`);

/* Who on the desk takes it up, and the code that releases it. */
const RUNNER = 'Rosa';
const pickupCode = pkg => String(4800 + pkg.id * 37).slice(-4);

const ACTIVE = [
    {
        id: 1,
        arrival_date: "October 1, 2024",
        arrival_time: "2:30 PM",
        sender: "Amazon",
        package_type: "Small Box",
        image_url: Package0,
        carrier: 'Amazon Logistics', tracking: 'TBA318840219000', shelf: 'Shelf B-3', spot: 'on shelf B-3', receivedBy: 'Ana, front desk', weight: '2.1 lb'
    },
    {
        id: 2,
        arrival_date: "September 29, 2024",
        arrival_time: "10:15 AM",
        sender: "Amazon",
        package_type: "Envelope",
        image_url: Package1,
        carrier: 'Amazon Logistics', tracking: 'TBA318512770041', shelf: 'Mail room, slot 12', spot: 'in the mail room, slot 12', receivedBy: 'Marcus, front desk', weight: '0.4 lb'
    }
];

const HISTORY = [
    { id: 3, arrival_date: "September 25, 2024", pickup_date: "September 26, 2024", sender: "Amazon", package_type: "Medium Box", image_url: Package2, carrier: 'Amazon Logistics', tracking: 'TBA317990142200', pickedUpBy: 'Alex Rivera' },
    { id: 4, arrival_date: "September 20, 2024", pickup_date: "September 21, 2024", sender: "Amazon", package_type: "Large Box", image_url: Package3, carrier: 'Amazon Logistics', tracking: 'TBA317604418813', pickedUpBy: 'Delivered to 1204 by Rosa' },
    { id: 5, arrival_date: "September 15, 2024", pickup_date: "September 16, 2024", sender: "Amazon", package_type: "Book Envelope", image_url: Package4, carrier: 'USPS', tracking: '9405 5036 9930 0187 2213 54', pickedUpBy: 'Alex Rivera' },
    { id: 6, arrival_date: "September 10, 2024", pickup_date: "September 11, 2024", sender: "Amazon", package_type: "Small Box", image_url: Package5, carrier: 'Amazon Logistics', tracking: 'TBA316880512096', pickedUpBy: 'Alex Rivera' },
    { id: 7, arrival_date: "September 5, 2024", pickup_date: "September 6, 2024", sender: "FedEx", package_type: "Envelope", image_url: Package6, carrier: 'FedEx Express', tracking: '7741 2290 8815', pickedUpBy: 'Jordan Blake, authorized' },
    { id: 8, arrival_date: "August 30, 2024", pickup_date: "August 31, 2024", sender: "UPS", package_type: "Large Box", image_url: Package7, carrier: 'UPS Ground', tracking: '1Z 999 AA1 01 2345 6784', pickedUpBy: 'Delivered to 1204 by Rosa' },
    { id: 9, arrival_date: "August 25, 2024", pickup_date: "August 26, 2024", sender: "DHL", package_type: "Medium Box", image_url: Package8, carrier: 'DHL Express', tracking: '3521 8840 77', pickedUpBy: 'Alex Rivera' },
    { id: 10, arrival_date: "August 20, 2024", pickup_date: "August 21, 2024", sender: "Target", package_type: "Small Box", image_url: Package9, carrier: 'Shipt', tracking: 'TGT-88120443', pickedUpBy: 'Alex Rivera' }
];

const SENDERS = ['All', ...new Set(HISTORY.map(p => p.sender))];

function PackagesScreen() {
    const [flash, notify] = useFlash();
    const [active, setActive] = useState(ACTIVE);
    const [searching, setSearching] = useState(false);
    const [query, setQuery] = useState('');
    const [sender, setSender] = useState('All');
    const sheet = useModal();
    const reduced = useReducedMotion();

    /* Search reads everything a resident might remember a package by:
       who sent it, what it was, which carrier and either date. */
    const q = query.trim().toLowerCase();
    const history = HISTORY.filter(p => (sender === 'All' || p.sender === sender) && (!q || [
        p.sender, p.package_type, p.carrier, p.arrival_date, p.pickup_date, p.tracking
    ].some(text => text.toLowerCase().includes(q))));

    const closeSearch = () => {
        setSearching(false);
        setQuery('');
        setSender('All');
    };

    const plan = (pkg, next) => {
        setActive(list => list.map(p => (p.id === pkg.id ? { ...p, plan: next } : p)));
        notify(next.kind === 'deliver'
            ? `${pkg.sender} ${pkg.package_type.toLowerCase()} coming up to ${RESIDENT.unit} ${spoken(next.window)}.`
            : `${pkg.sender} ${pkg.package_type.toLowerCase()} is held at the desk for you. Code ${pickupCode(pkg)}.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead
                eyebrow="Front desk"
                title={`You have ${active.length} ${active.length === 1 ? 'package' : 'packages'} waiting`}
            />
            {flash}

            <div className="package-grid package-grid--active">
                {active.map((pkg, index) => (
                    <Rise className="pkg-cell" key={pkg.id} index={index}>
                        <button
                            type="button"
                            className="current-package-item pkg-card"
                            onClick={() => sheet.open({ pkg, past: false })}
                        >
                            <img src={pkg.image_url} alt="" className="current-package-image" loading="lazy" />
                            <div className="package-info">
                                <h5>{pkg.sender}</h5>
                                <p><strong>Arrived:</strong> {pkg.arrival_date} at {pkg.arrival_time}</p>
                                <p><strong>Type:</strong> {pkg.package_type}</p>
                            </div>
                            <span className="pkg-card__foot">
                                {pkg.plan?.kind === 'deliver'
                                    ? <Chip tone="ok">Delivering {pkg.plan.window === WINDOWS[0] ? 'soon' : pkg.plan.window}</Chip>
                                    : pkg.plan?.kind === 'pickup'
                                        ? <Chip tone="info">Held at the desk</Chip>
                                        : <Chip tone="muted">{pkg.shelf}</Chip>}
                                <span className="pkg-card__more">Details</span>
                            </span>
                        </button>
                    </Rise>
                ))}
            </div>

            <div className="screen__head" style={{ marginTop: 'clamp(2rem, 4vw, 3rem)' }}>
                <h3>Recent package history</h3>
                <button
                    type="button"
                    className="btn btn-outline-light"
                    aria-expanded={searching}
                    onClick={() => (searching ? closeSearch() : setSearching(true))}
                >
                    {searching ? 'Close search' : 'Search history'}
                </button>
            </div>

            <AnimatePresence initial={false}>
                {searching && (
                    <motion.div
                        className="pkg-search"
                        initial={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                        transition={REVEAL}
                    >
                        <div className="pkg-search__inner">
                            <input
                                type="search"
                                className="form-control"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search by sender, type, carrier, date or tracking number"
                                aria-label="Search package history"
                                autoFocus
                            />
                            <Segments label="Sender" options={SENDERS} value={sender} onChange={setSender} />
                            <span className="row__meta">
                                {history.length === HISTORY.length
                                    ? `All ${HISTORY.length} packages from the last 90 days`
                                    : `${history.length} of ${HISTORY.length} packages`}
                            </span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {history.length === 0 ? (
                <Empty>No packages match{q ? ` "${query.trim()}"` : ''}{sender !== 'All' ? ` from ${sender}` : ''}.</Empty>
            ) : (
                <div className="package-grid">
                    {history.map((pkg, index) => (
                        <Rise className="pkg-cell" key={pkg.id} index={index}>
                            <button
                                type="button"
                                className="history-package-item pkg-card"
                                onClick={() => sheet.open({ pkg, past: true })}
                            >
                                <img src={pkg.image_url} alt="" className="history-package-image" loading="lazy" />
                                <div className="history-package-info">
                                    <p><strong>From:</strong> {pkg.sender}</p>
                                    <p><strong>Arrived:</strong> {pkg.arrival_date}</p>
                                    <p><strong>Picked up:</strong> {pkg.pickup_date}</p>
                                    <p><strong>Type:</strong> {pkg.package_type}</p>
                                </div>
                            </button>
                        </Rise>
                    ))}
                </div>
            )}

            <PackageSheet
                item={sheet.item}
                onClose={sheet.close}
                current={sheet.item && !sheet.item.past ? active.find(p => p.id === sheet.item.pkg.id) : null}
                onPlan={plan}
            />
        </div>
    );
}

/* One package, opened. A waiting one can be picked up or sent upstairs;
   one already collected shows who took it, and can be queried. */
function PackageSheet({ item, onClose, current, onPlan }) {
    const shown = useLast(item);
    const [view, setView] = useState('details');
    const [when, setWhen] = useState(WINDOWS[0]);
    const [openFor, setOpenFor] = useState(null);

    /* Every opening starts on the details, not on the last one's tick.
       useModal hands over a fresh object each time, so identity is the
       signal; this is React's adjust-state-while-rendering pattern. */
    if (item !== openFor) {
        setOpenFor(item);
        if (item) {
            setView('details');
            setWhen(item.pkg.plan?.window || WINDOWS[0]);
        }
    }

    if (!shown) return <Modal open={false} onClose={onClose} />;

    const pkg = current || shown.pkg;
    const past = shown.past;
    const code = pickupCode(pkg);

    const deliver = () => { onPlan(pkg, { kind: 'deliver', window: when }); setView('delivering'); };
    const pickup = () => { onPlan(pkg, { kind: 'pickup' }); setView('held'); };

    let actions;
    if (view !== 'details') {
        actions = <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>;
    } else if (past) {
        actions = (
            <>
                <button type="button" className="btn btn-outline-light" onClick={() => setView('reported')}>Report a problem</button>
                <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
            </>
        );
    } else {
        actions = (
            <>
                <button type="button" className="btn btn-outline-light" onClick={pickup}>I'll pick it up</button>
                <button type="button" className="btn btn-primary" onClick={deliver}>Deliver to my door</button>
            </>
        );
    }

    return (
        <Modal
            open={Boolean(item)}
            onClose={onClose}
            eyebrow={past ? 'Package history' : 'Waiting at the front desk'}
            title={`${pkg.sender} · ${pkg.package_type}`}
            actions={actions}
        >
            <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={view}
                    className="stack"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3, ease: EASE }}
                >
                    {view === 'details' && (
                        <>
                            <img className="modal__media pkg-sheet__photo" src={pkg.image_url} alt={`${pkg.sender} ${pkg.package_type.toLowerCase()}`} />
                            <div className="row__end" style={{ justifyContent: 'flex-start' }}>
                                {past ? <Chip tone="ok">Picked up {pkg.pickup_date}</Chip> : <Chip tone="info">{pkg.shelf}</Chip>}
                                <Chip tone="muted">{pkg.carrier}</Chip>
                            </div>
                            <dl className="act-facts">
                                <div><dt>Sender</dt><dd>{pkg.sender}</dd></div>
                                <div><dt>Arrived</dt><dd>{pkg.arrival_date}{pkg.arrival_time ? `, ${pkg.arrival_time}` : ''}</dd></div>
                                {past
                                    ? <div><dt>Collected</dt><dd>{pkg.pickedUpBy}</dd></div>
                                    : <div><dt>Signed for by</dt><dd>{pkg.receivedBy}</dd></div>}
                                {!past && <div><dt>Weight</dt><dd>{pkg.weight}</dd></div>}
                                <div className="act-facts__wide"><dt>Tracking</dt><dd>{pkg.tracking}</dd></div>
                            </dl>

                            {!past && (
                                <div className="stack" style={{ gap: '0.5rem' }}>
                                    <span className="form-label">Deliver to {RESIDENT.unit}</span>
                                    <Segments label="Delivery time" options={WINDOWS} value={when} onChange={setWhen} />
                                    <p className="row__meta">
                                        {current?.plan?.kind === 'deliver'
                                            ? `Booked ${spoken(current.plan.window)}. Choose again to change it.`
                                            : `${RUNNER} on the desk brings it up and leaves it inside your door if you are out.`}
                                    </p>
                                </div>
                            )}
                        </>
                    )}

                    {view === 'delivering' && (
                        <Done title={when === WINDOWS[0] ? 'Coming up shortly' : `Coming up ${spoken(when)}`}>
                            <p>
                                {RUNNER} will bring your {pkg.sender} {pkg.package_type.toLowerCase()} to
                                residence {RESIDENT.unit} {spoken(when)}.
                                You will get a text when it is at your door.
                            </p>
                        </Done>
                    )}

                    {view === 'held' && (
                        <Done title="Held at the front desk">
                            <p>Show this code at the desk, or just give your name. It is <span className="act-nowrap">{pkg.spot}</span>.</p>
                            <span className="pkg-code" aria-label={`Pickup code ${code.split('').join(' ')}`}>
                                {code.split('').map((digit, i) => <span key={i}>{digit}</span>)}
                            </span>
                        </Done>
                    )}

                    {view === 'reported' && (
                        <Done title="The desk is on it">
                            <p>
                                We have flagged the {pkg.sender} {pkg.package_type.toLowerCase()} from {pkg.arrival_date}.
                                The front desk will call you today and can open a claim with {pkg.carrier}.
                            </p>
                        </Done>
                    )}
                </motion.div>
            </AnimatePresence>
        </Modal>
    );
}

export default PackagesScreen;
